import type { DatabaseExplorerNode, MessageTreeNode } from '../../explorer/models/DatabaseTreeModels';
import { createDatabaseRoleNode, createDatabaseUserNode, createGroupRoleNode, createLoginNode, createLoginRoleNode, createOracleRoleNode, createOracleUserNode, createSchemaNode, createServerRoleNode } from '../../explorer/models/securityNodeFactories';
import type { SecurityPrincipal } from '../../../types/security';
import { sanitizeConnectionErrorMessage } from './connectionErrorUtils';

const SECURITY_LEAF_FOLDER_TYPES = ['logins-folder', 'server-roles-folder', 'login-roles-folder', 'group-roles-folder', 'users-folder', 'database-roles-folder', 'schemas-folder'] as const;

export type SecurityLeafFolderNodeType = (typeof SECURITY_LEAF_FOLDER_TYPES)[number];

export function isSecurityLeafFolderNodeType(nodeType: string): nodeType is SecurityLeafFolderNodeType {
  return SECURITY_LEAF_FOLDER_TYPES.includes(nodeType as SecurityLeafFolderNodeType);
}

const SECURITY_FOLDER_LABEL: Record<SecurityLeafFolderNodeType, string> = {
  'logins-folder': 'logins',
  'server-roles-folder': 'server roles',
  'login-roles-folder': 'login roles',
  'group-roles-folder': 'group roles',
  'users-folder': 'users',
  'database-roles-folder': 'database roles',
  'schemas-folder': 'schemas',
};

const DATABASE_REQUIRED_FOLDERS = new Set<SecurityLeafFolderNodeType>(['users-folder', 'database-roles-folder', 'schemas-folder']);

export function needsDatabaseForSecurityFolder(folderType: SecurityLeafFolderNodeType): boolean { return DATABASE_REQUIRED_FOLDERS.has(folderType); }

type MessageVariant = 'message' | 'error';

function createFolderMessageNode(options: {
  connectionId: string;
  database?: string;
  folderType: SecurityLeafFolderNodeType;
  parentId: string;
  message: string;
  variant: MessageVariant;
}): MessageTreeNode {
  const { connectionId, database, folderType, parentId, message, variant } = options;
  return {
    id: `${variant}-${connectionId}-${database ?? 'server'}-${folderType}`,
    parentId,
    name: message,
    type: variant,
    canHaveChildren: false,
    children: [],
    isExpanded: false,
    icon: variant === 'message' ? 'message' : undefined,
    metadata: { connectionId, database, error: variant === 'error' ? message : undefined },
  };
}

export function createSecurityFolderEmptyNode(options: {
  connectionId: string;
  database?: string;
  folderType: SecurityLeafFolderNodeType;
  parentId: string;
}): MessageTreeNode {
  return createFolderMessageNode({ ...options, message: `No ${SECURITY_FOLDER_LABEL[options.folderType]} found`, variant: 'message' });
}

export function createSecurityFolderErrorNode(options: {
  connectionId: string;
  database?: string;
  folderType: SecurityLeafFolderNodeType;
  parentId: string;
  errorMessage?: string;
}): MessageTreeNode {
  const fallback = `Failed to load ${SECURITY_FOLDER_LABEL[options.folderType]}`;
  const rawMessage = options.errorMessage || fallback;
  const sanitized = sanitizeConnectionErrorMessage(rawMessage);
  return createFolderMessageNode({ ...options, message: `Error: ${sanitized}`, variant: 'error' });
}

const sortByName = <T extends { name: string }>(items: T[]) =>
  items.slice().sort((a, b) => a.name.localeCompare(b.name));

async function fetchPrincipals(options: {
  api: NonNullable<Window['electronAPI']>['securityManagement'];
  folderType: Exclude<SecurityLeafFolderNodeType, 'schemas-folder'>;
  connectionId: string;
  database?: string;
}): Promise<{ principals: SecurityPrincipal[]; toNode: (principal: SecurityPrincipal) => DatabaseExplorerNode }> {
  const { api, folderType, connectionId, database } = options;

  switch (folderType) {
    case 'logins-folder': {
      const result = await api.fetchLogins({ connectionId, database });
      if (!result.success) throw new Error(result.error || 'Failed to fetch logins');
      return {
        principals: result.logins ?? [],
        toNode: (principal) => {
          if (principal.engine === 'oracle') return createOracleUserNode(connectionId, principal.name, principal.isSystem ?? false);
          return createLoginNode(connectionId, principal.name, principal.id, principal.isSystem ?? false);
        },
      };
    }
    case 'server-roles-folder': {
      const result = await api.fetchServerRoles({ connectionId, database });
      if (!result.success) throw new Error(result.error || 'Failed to fetch server roles');
      return {
        principals: result.roles ?? [],
        toNode: (principal) => {
          if (principal.engine === 'oracle') return createOracleRoleNode(connectionId, principal.name, principal.isSystem ?? false);
          return createServerRoleNode(connectionId, principal.name, principal.id, principal.isSystem ?? false);
        },
      };
    }
    case 'login-roles-folder': {
      const result = await api.fetchLogins({ connectionId, database });
      if (!result.success) throw new Error(result.error || 'Failed to fetch login roles');
      return { principals: result.logins ?? [], toNode: (principal) => createLoginRoleNode(connectionId, principal.name, principal.id, principal.isSystem ?? false) };
    }
    case 'group-roles-folder': {
      const result = await api.fetchServerRoles({ connectionId, database });
      if (!result.success) throw new Error(result.error || 'Failed to fetch group roles');
      return { principals: result.roles ?? [], toNode: (principal) => createGroupRoleNode(connectionId, principal.name, principal.id, principal.isSystem ?? false) };
    }
    case 'users-folder': {
      const result = await api.fetchUsers({ connectionId, database: database as string });
      if (!result.success) throw new Error(result.error || 'Failed to fetch users');
      return { principals: result.users ?? [], toNode: (principal) => createDatabaseUserNode(connectionId, database as string, principal.name, principal.id, principal.isSystem ?? false) };
    }
    case 'database-roles-folder': {
      const result = await api.fetchDatabaseRoles({ connectionId, database: database as string });
      if (!result.success) throw new Error(result.error || 'Failed to fetch database roles');
      return { principals: result.roles ?? [], toNode: (principal) => createDatabaseRoleNode(connectionId, database as string, principal.name, principal.id, principal.engine, principal.isSystem ?? false) };
    }
  }
}

export async function fetchSecurityFolderChildren(options: {
  api: NonNullable<Window['electronAPI']>['securityManagement'];
  folderType: SecurityLeafFolderNodeType;
  connectionId: string;
  database?: string;
  parentId: string;
}): Promise<DatabaseExplorerNode[]> {
  const { api, folderType, connectionId, database, parentId } = options;

  if (folderType === 'schemas-folder') {
    const result = await api.fetchSchemas({ connectionId, database: database as string });
    if (!result.success) throw new Error(result.error || 'Failed to fetch schemas');

    const schemas = result.schemas ?? [];
    return schemas.length === 0
      ? [createSecurityFolderEmptyNode({ connectionId, database, folderType, parentId })]
      : sortByName(schemas).map((schema) => createSchemaNode(connectionId, database as string, schema.name, schema.id, schema.engine, schema.isSystem));
  }

  const { principals, toNode } = await fetchPrincipals({
    api,
    folderType,
    connectionId,
    database,
  });

  return principals.length === 0
    ? [createSecurityFolderEmptyNode({ connectionId, database, folderType, parentId })]
    : sortByName(principals).map((principal) => toNode(principal));
}
